import { QueryClient, useQuery, useQueryClient } from 'react-query';

import { GqlRequestOptions } from '@common/api-request';
import { GetPdpBaseInfo_catalog_product_base_info, PDP_BASE_INFO_QUERY_KEY, queryOptions } from '@domains/pdp-common';

import { getContactInfo } from '../graphql/contact-info.2';
import { GetContactInfo_pdp_base_info } from '../graphql/contact-info_types.2';

interface Param {
  catalog_product_id: string;
}

const getInitialData = (
  cachedData?: GetPdpBaseInfo_catalog_product_base_info,
  data?: GetContactInfo_pdp_base_info | null,
) => {
  const getProductData = (source: typeof cachedData | typeof data) => ({
    id: source?.product?.id,
    fulfillment_type: source?.product?.fulfillment_type,
    external_product_url: source?.product?.external_product_url,
    essentials: source?.product?.essentials ?? [],
    entry_type: source?.product?.entry_type,
  });

  const getShopData = (source: typeof cachedData | typeof data) => ({
    parent_id: source?.shop?.parent_id,
    customer_center: source?.shop?.customer_center ?? null,
    main_contact: source?.shop?.main_contact ?? null,
    customer_center_tel: source?.shop?.customer_center_tel ?? null,
    sale_type: source?.shop?.sale_type,
    business_license: source?.shop?.business_license,
    shop_business_info: source?.shop?.shop_business_info,
    online_sales_license: source?.shop?.online_sales_license,
  });

  const product = cachedData?.product ? getProductData(cachedData) : getProductData(data);
  const shop = cachedData?.shop ? getShopData(cachedData) : getShopData(data);
  const policy_list = cachedData?.policy_list ?? data?.policy_list ?? [];

  return {
    product,
    shop,
    policy_list,
  };
};

export const QUERY_KEY = ['getContactInfo'];

export const prefetchContactInfo = (params: Param, options: GqlRequestOptions, query_client: QueryClient) => {
  const query_options = getContactInfoQueryOption(params, options);

  return query_client.prefetchQuery(query_options);
};

function getContactInfoQueryOption(params: Param, options?: GqlRequestOptions) {
  return {
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data } = await getContactInfo(params, { ...options, show_alert: false });

      return data.pdp_base_info;
    },
    ...queryOptions,
  };
}

export const useContactInfo = (param: Param, options?: GqlRequestOptions) => {
  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData<GetPdpBaseInfo_catalog_product_base_info>(PDP_BASE_INFO_QUERY_KEY);
  const { queryKey, queryFn, ...otherOptions } = getContactInfoQueryOption(param, options);
  const enabled = !cachedData?.shop;

  const { data } = useQuery(queryKey, queryFn, {
    enabled,
    ...otherOptions,
  });
  return getInitialData(cachedData, data);
};
